import ColorScheme from 'mini-color-utils/lib/ColorScheme';
import React, { useContext, useState } from 'react';
import strings from 'owa-strings/owa-mini-common-strings';
import { addAriaLiveLabel } from 'mini-accessibility';
import { CalendarEvent } from 'mini-calendar-event-types';
import { getDefaultMiniColorScheme } from 'mini-color-utils/lib/colorUtils';
import { getResponseTypeFromAction } from '../utils/getResponseTypeFromAction';
import { ReactAppCtx } from 'mini-app-context';
import { ReactAppSourceContext } from 'data/lib/utils/ReactAppSourceContext';
import { RSVP_EVENT_SEND } from 'data/lib/mini-app-constants/commonEventSignalConstants';
import { RsvpAction } from '../schema/RsvpAction';
import { RsvpMaybeButton, RsvpNoButton, RsvpYesButton } from './RsvpActionButtons';
import { RsvpStringProps } from '../schema/RsvpString.types';
import { ToggleHubItem } from 'mini-hub';
import { useStopEventBackgroundPropagation } from 'mini-hooks/lib/useStopEventBackgroundPropagation';

const DEFAULT_MAX_ALLOWED_CHARS = 280;

const styles = require('./RsvpActions.scss');

export interface RsvpActionsProps {
    /**
     * The calendar event
     */
    item: CalendarEvent;
    /**
     * Callback handler to be triggered when user starts adding a message to organizer
     */
    onAddMessageFocus: () => void;
    /**
     * Handler called on click of Rsvp actions
     */
    onRsvpAction: (actionType: RsvpAction, updateOrganizer?: boolean, message?: string) => void;
    /**
     * String props for localization of the text used in component
     */
    stringProps: RsvpStringProps;
    /**
     *  Tells whether message to organizer is allowed
     */
    isMessageToOrganizerAllowed?: boolean;
    /**
     * Whether to show email organizer toggle set or unset
     * ( This property is used only when isMessageToOrganizerAllowed is true)
     */
    defaultValueUpdateOrganizerSetting?: boolean;
    /**
     *  Max chars allowed for message to organizer
     */
    maxCharsAllowed?: number;
    /**
     * color scheme for the event
     */
    colorScheme?: ColorScheme;
}

export const RsvpActions = (props: RsvpActionsProps) => {
    const {
        onAddMessageFocus,
        onRsvpAction,
        stringProps,
        defaultValueUpdateOrganizerSetting,
        isMessageToOrganizerAllowed,
        maxCharsAllowed,
        colorScheme = getDefaultMiniColorScheme(),
        item,
    } = props;

    const {
        emailOrganizer: emailOrganizerString,
        yesButtonTitle,
        maybeButtonTitle,
        noButtonTitle,
        messagePlaceholder,
        yesButtonAriaTitle,
        maybeButtonAriaTitle,
        noButtonAriaTitle,
    } = stringProps;

    const commentInputRef = useStopEventBackgroundPropagation();
    const [emailOrganizer, setEmailOrganizer] = useState<boolean>(
        !!defaultValueUpdateOrganizerSetting
    );

    const finalMaxCharsAllowed = maxCharsAllowed || DEFAULT_MAX_ALLOWED_CHARS;

    const ctx = useContext(ReactAppCtx);
    const actionSource = useContext(ReactAppSourceContext);

    const onHandleAction = (actionType: RsvpAction) => {
        if (onRsvpAction) {
            const hasValidComment =
                !!commentInputRef.current &&
                commentInputRef.current.value !== commentInputRef.current.defaultValue;
            ctx?.signal(
                RSVP_EVENT_SEND,
                {
                    isUIAction: true,
                    interactionType: getResponseTypeFromAction(actionType),
                    hasComment: hasValidComment,
                    eventId: item?.ItemId?.Id,
                },
                { actionSource, isCore: true }
            );
            onRsvpAction(
                actionType,
                emailOrganizer,
                hasValidComment ? commentInputRef.current.value : null
            );
        }
    };

    const onYesAction = () => {
        onHandleAction(RsvpAction.YES);
    };

    const onMaybeAction = () => {
        onHandleAction(RsvpAction.MAYBE);
    };

    const onNoAction = () => {
        onHandleAction(RsvpAction.NO);
    };

    const toggleEmailOrganizerSetting = () => {
        addAriaLiveLabel(!emailOrganizer ? strings.toggleChangedToOn : strings.toggleChangedToOff);
        setEmailOrganizer(!emailOrganizer);
        ctx?.signal(
            'EmailOrganizerToggleClicked',
            { value: emailOrganizer, isUIAction: true },
            { actionSource }
        );
    };

    const setTextAreaHeight = () => {
        commentInputRef.current &&
            (commentInputRef.current.style.height =
                Math.min(commentInputRef.current.scrollHeight, 58) + 'px');
    };

    return (
        <div className={styles.rsvpActionsContainer}>
            {isMessageToOrganizerAllowed ? (
                <>
                    <ToggleHubItem
                        primaryText={emailOrganizerString}
                        defaultChecked={emailOrganizer}
                        isChecked={emailOrganizer}
                        useCheckedStateFromProps
                        color={colorScheme.schemePrimary}
                        onClick={toggleEmailOrganizerSetting}
                        styles={{
                            iconContainerStyles: styles.toggleHubItemIconContainer,
                            className: styles.toggleHubItemContainer,
                        }}
                    />
                    {emailOrganizer && (
                        <textarea
                            className={styles.textArea}
                            placeholder={messagePlaceholder}
                            maxLength={finalMaxCharsAllowed}
                            onInput={setTextAreaHeight}
                            onFocus={onAddMessageFocus}
                            onClick={ev => ev?.stopPropagation()}
                            ref={commentInputRef}
                        />
                    )}
                </>
            ) : (
                <div className={styles.noResponseRequested}>
                    {strings.emailOrganizerUnavailable}
                </div>
            )}
            <div className={styles.rsvpActionButtonsContainer}>
                <RsvpYesButton
                    title={yesButtonTitle}
                    onAction={onYesAction}
                    titleAriaLabel={yesButtonAriaTitle || yesButtonTitle}
                />
                <RsvpMaybeButton
                    title={maybeButtonTitle}
                    onAction={onMaybeAction}
                    titleAriaLabel={maybeButtonAriaTitle || maybeButtonTitle}
                />
                <RsvpNoButton
                    title={noButtonTitle}
                    onAction={onNoAction}
                    titleAriaLabel={noButtonAriaTitle || noButtonTitle}
                />
            </div>
        </div>
    );
};
